/**
 * NotificationPrompt — first-run toast that invites the user to turn
 * on push notifications. Appears:
 *
 *   - Once per signed-in user (suppressed forever after an answer).
 *   - Only if browser + SW + PushManager are supported.
 *   - Only if permission === "default" (so we never re-ask denied users).
 *
 * It's deliberately low-pressure: a small anchored toast at the bottom
 * right (or above the floating nav on mobile) with a single accept
 * button and a dismiss. Rejecting stores "declined" in localStorage so
 * we leave the user alone.
 */

import React, { useEffect, useState } from "react";
import { Bell, BellOff, X, Share } from "lucide-react";

import { enablePush, pushState, iosPushStatus } from "@/lib/push";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/Toast";

const KEY = "cl.pushPrompt.declined";
type Mode = "hidden" | "standard" | "ios-install";

export default function NotificationPrompt() {
  const { isAuthed, user } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("hidden");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthed || !user) return;
    const declined = localStorage.getItem(`${KEY}:${user.id}`);
    if (declined) return;

    // iOS branch: if user is on iOS Safari in a regular tab (not standalone),
    // Web Push won't work even if we try. Show install-first guidance instead
    // of a dead "Turn on" button.
    const ios = iosPushStatus();
    if (ios === "install-required") {
      const t = window.setTimeout(() => setMode("ios-install"), 4500);
      return () => window.clearTimeout(t);
    }
    if (ios === "unsupported") return; // iOS < 16.4 — nothing we can do

    const s = pushState();
    if (!s.supported || s.missingKey) return;
    if (s.permission !== "default") return;

    const t = window.setTimeout(() => setMode("standard"), 4500);
    return () => window.clearTimeout(t);
  }, [isAuthed, user?.id]);

  const show = mode !== "hidden";

  const dismiss = () => {
    if (user?.id) localStorage.setItem(`${KEY}:${user.id}`, "1");
    setMode("hidden");
  };

  const accept = async () => {
    setBusy(true);
    const res = await enablePush();
    setBusy(false);
    if (res.ok) {
      toast.success("Notifications on", "We'll ping you when work's ready.");
      dismiss();
    } else if (res.reason === "denied") {
      dismiss();
    } else if (res.reason === "unsupported") {
      toast.error("Not supported on this browser", "Try Chrome, Safari iOS 16.4+ installed, or Edge.");
    } else {
      toast.error("Couldn't enable notifications", res.message || "Something went wrong.");
    }
  };

  if (!show) return null;

  // iOS install-first variant
  if (mode === "ios-install") {
    return (
      <div
        role="dialog"
        aria-label="Install CanvaLoop to enable notifications"
        className="cl-push-prompt"
        style={{
          position: "fixed",
          zIndex: 65,
          right: 16,
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)",
          maxWidth: "calc(100vw - 32px)",
          width: 360,
          padding: 16,
          background: "var(--bg-surface-raised)",
          border: "1px solid var(--border-default)",
          borderRadius: 18,
          boxShadow: "0 22px 40px -10px rgba(0,0,0,0.28)",
          animation: "cl-push-in 360ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{
            width: 44, height: 44, flexShrink: 0, borderRadius: 12,
            background: "var(--brand-gradient)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff",
          }}>
            <Share size={20} strokeWidth={1.75} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: "var(--font-size-md)", fontWeight: 700,
              fontFamily: "var(--font-display)",
              color: "var(--text-primary)", marginBottom: 2,
            }}>
              Install to get notifications
            </div>
            <div style={{
              fontSize: "var(--font-size-sm)", color: "var(--text-secondary)",
              lineHeight: 1.45,
            }}>
              Tap <Share size={11} style={{ verticalAlign: "-1px" }} /> <strong>Share</strong>{" "}
              → <strong>Add to Home Screen</strong>.
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            style={{
              width: 28, height: 28, borderRadius: "50%",
              background: "transparent", border: "none",
              color: "var(--text-tertiary)", cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
        <style>{`
          @keyframes cl-push-in {
            from { opacity: 0; transform: translateY(10px) scale(0.96); }
            to   { opacity: 1; transform: translateY(0)   scale(1); }
          }
          @media (min-width: 1024px) {
            .cl-push-prompt { bottom: 24px !important; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Enable notifications"
      className="cl-push-prompt"
      style={{
        position: "fixed",
        zIndex: 65,
        right: 16,
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)",
        maxWidth: "calc(100vw - 32px)",
        width: 360,
        padding: 16,
        background: "var(--bg-surface-raised)",
        border: "1px solid var(--border-default)",
        borderRadius: 18,
        boxShadow: "0 22px 40px -10px rgba(0,0,0,0.28)",
        backdropFilter: "blur(16px) saturate(160%)",
        WebkitBackdropFilter: "blur(16px) saturate(160%)",
        animation: "cl-push-in 360ms cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            width: 44, height: 44,
            flexShrink: 0,
            borderRadius: 12,
            background: "var(--brand-gradient)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff",
            boxShadow: "0 10px 22px -8px rgba(197,63,255,0.45)",
          }}
        >
          <Bell size={20} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: "var(--font-size-md)",
            fontWeight: 700,
            fontFamily: "var(--font-display)",
            color: "var(--text-primary)",
            marginBottom: 2,
          }}>
            Get pinged when work's ready
          </div>
          <div style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--text-secondary)",
            lineHeight: 1.45,
          }}>
            Outputs land while you're away.
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            width: 28, height: 28,
            borderRadius: "50%",
            background: "transparent",
            border: "none",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button
          onClick={dismiss}
          style={{
            flex: 1,
            height: 40,
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-default)",
            borderRadius: 12,
            color: "var(--text-secondary)",
            fontSize: "var(--font-size-sm)",
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
        >
          <BellOff size={13} strokeWidth={1.75} />
          Not now
        </button>
        <button
          onClick={accept}
          disabled={busy}
          style={{
            flex: 1.3,
            height: 40,
            background: "var(--brand-gradient)",
            border: "none",
            borderRadius: 12,
            color: "#fff",
            fontSize: "var(--font-size-sm)",
            fontWeight: 700,
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.7 : 1,
            boxShadow: "0 10px 22px -6px rgba(197,63,255,0.4)",
          }}
        >
          {busy ? "Enabling…" : "Turn on"}
        </button>
      </div>

      <style>{`
        @keyframes cl-push-in {
          from { opacity: 0; transform: translateY(10px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @media (min-width: 1024px) {
          .cl-push-prompt { bottom: 24px !important; }
        }
      `}</style>
    </div>
  );
}
